import { memoize, shallowMemoize } from '@sail/utils';
import { NavigationError } from '../errors';
import addCarryOverIdentifiers from '../internal/addCarryOverIdentifiers';
import carryOverIdentificator from '../internal/carryOverIdentificator';
import toTree from '../internal/compiler/toTree';
import tokenizePath from '../internal/compiler/tokenizePath';
import fullLocationToUrl from '../internal/fullLocationToUrl';
import groupSeparator from '../internal/groupSeparator';
import memoizeConfig from '../internal/memoizeConfig';
import replaceGlobal from '../internal/replaceGlobal';
import stripTrailingSlash from '../internal/stripTrailingSlash';
export const parsePath = memoize((path)=>{
    return addCarryOverIdentifiers(replaceGlobal(stripTrailingSlash(path)));
});
const getRegexFromRoute = memoizeConfig((config, name)=>{
    const routeEntry = config.routes[name];
    return parsePath(typeof routeEntry === 'string' ? routeEntry : routeEntry[0]);
});
const checkValidValue = shallowMemoize((id, value, pattern)=>{
    if (!pattern) {
        if (id.endsWith('+') && (!value || Array.isArray(value) && value.length === 0)) {
            throw new TypeError(`Cannot generate the param ${id} with missing param: ${pattern}`);
        }
        return;
    }
    if (typeof value !== 'string' || !pattern.test(value)) {
        throw new TypeError(`Cannot generate the param ${id} with missing param: ${pattern}`);
    }
});
const valueToString = shallowMemoize((value)=>{
    return typeof value === 'string' ? encodeURIComponent(value) : value.map(encodeURIComponent).join('/');
});
const optionalChars = new Set([
    '?',
    '!',
    '*'
]);
const isOptionalParam = shallowMemoize((idParam)=>{
    return optionalChars.has(idParam.charAt(idParam.length - 1));
});
const modifierChars = new Set([
    '*',
    '+',
    '?'
]);
const stripModifier = shallowMemoize((id)=>{
    const lastChar = id.charAt(id.length - 1);
    return modifierChars.has(lastChar) ? id.slice(0, -1) : id;
});
function processPathParams(part, params, meta) {
    if (!meta) {
        return part;
    }
    const idAndPattern = meta.idAndPattern;
    const id = meta.id;
    const pattern = meta.pattern;
    const value = params[stripModifier(id)] || '';
    if (!value && !isOptionalParam(id)) {
        if (!meta.isOptional) {
            throw new TypeError(`Missing value for the mandatory param "${id}" in the path`);
        }
        return '';
    }
    if (value) {
        checkValidValue(id, value, pattern);
    }
    return part.replace(idAndPattern, valueToString(value));
}
export function tokensToPath(tokens, params) {
    let resultPath = '';
    for(let i = 0; i < tokens.length; i++){
        const [token, meta] = tokens[i];
        const pathResult = processPathParams(token, params, meta);
        if (pathResult) {
            resultPath += `/${pathResult}`;
        }
    }
    return resultPath;
}
function routeToUrl(config, route, base = null) {
    const paramsPathToRegexp = {};
    const { name, params = {}, search, searchSafe, hash = '' } = config.unsafePreProcessRoute ? config.unsafePreProcessRoute(route, base) : route;
    const setParams = (params, { carryOver })=>{
        for(const key in params){
            if (params.hasOwnProperty(key)) {
                const value = params[key];
                const keyWithMaybeCarryOverIdentificator = `${carryOver ? carryOverIdentificator : ''}${key}`;
                if (Array.isArray(value) || typeof value !== 'object') {
                    paramsPathToRegexp[keyWithMaybeCarryOverIdentificator] = value;
                } else {
                    for(const sub in value){
                        if (value.hasOwnProperty(sub)) {
                            paramsPathToRegexp[`${keyWithMaybeCarryOverIdentificator}${groupSeparator}${sub}`] = value[sub];
                        }
                    }
                }
            }
        }
    };
    let pathname;
    if (!config.routes.hasOwnProperty(name)) {
        throw new ReferenceError(`Invalid route name passed: ${name}`);
    }
    try {
        if (base) {
            // copy over current route params as carry over params
            const baseParams = base.params || {};
            setParams(baseParams, {
                carryOver: true
            });
        }
        const paramsLocation = params;
        // copy over new route params as carry over params
        setParams(paramsLocation, {
            carryOver: true
        });
        // set new route params as not carry over params
        setParams(paramsLocation, {
            carryOver: false
        });
        const path = getRegexFromRoute(config, name);
        const tree = toTree(config.routes);
        var _tree_t_name;
        const tokens = (_tree_t_name = tree === null || tree === void 0 ? void 0 : tree.t[name]) !== null && _tree_t_name !== void 0 ? _tree_t_name : tokenizePath(path);
        pathname = tokensToPath(tokens, paramsPathToRegexp);
        // this a edge case scenario where ROOT would return an empty string.
        if (pathname === '') {
            pathname = '/';
        }
    } catch (error) {
        throw new NavigationError(`Cannot generate the route ${name} with given params`);
    }
    if (!pathname) {
        throw new NavigationError(`No path was generated for route ${name} with given params`);
    }
    const url = fullLocationToUrl({
        pathname,
        search: search !== null && search !== void 0 ? search : searchSafe,
        hash
    });
    return config.postProcessUrl ? config.postProcessUrl(url) : url;
}
export default memoizeConfig(routeToUrl);

//# sourceMappingURL=routeToUrl.js.map